/**
 * A widget's ⋯ menu (screenshots/89 "Export & share a chart"): Edit chart · Download PNG · Download SVG · Download CSV (data) ·
 * Copy link to chart · Print chart · Duplicate · Add to a dashboard… · Remove. PNG and SVG come from ECharts (getDataURL, the
 * SSR SVG renderer), CSV is the table view's rows with source + filter lines (exportChart.ts). Every action is confirmed with
 * a toast; Copy link shows the link in the toast where the clipboard isn't available. Non-chart widgets get the link, print,
 * duplicate and remove items. Opens as a menu: arrows move, Esc closes and returns focus to ⋯.
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toastStore } from "../stores/toastStore";
import { chartLink, download, fileSlug, toCSV, toPNG, toSVG } from "./charts/exportChart";
import { DATASETS } from "./charts/chartSettings";
import { zoomParam } from "./charts/interactions";
import { chartEditorStore } from "./chartEditorStore";
import { OvIcon } from "./icons";
import { overviewLayout, overviewStore } from "./overviewStore";
import type { LayoutItem } from "./types";
import { chartInstances, widgetViews } from "./widgetViews";

type Key = "edit" | "png" | "svg" | "csv" | "link" | "print" | "dup" | "add" | "remove";

export function WidgetMenu({ item, title, anchor, onClose, onAddToDashboard, readOnly = false }: { item: LayoutItem; title: string; anchor: HTMLElement | null; onClose: (focusBack: boolean) => void; onAddToDashboard?: (item: LayoutItem) => void;
  /** View-only dashboards and public links (Analytics): exports, link and print only. */
  readOnly?: boolean }) {
  const ref = useRef<HTMLUListElement>(null);
  const isChart = !!item.config.settings;
  const done = (message: string, open?: string) => toastStore.getState().show({ message, ...(open ? { actionLabel: "Open", onAction: () => window.open(open, "_blank", "noopener") } : {}) });

  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    // Only this menu's own ⋯ button toggles it; another widget's ⋯ closes this one (one menu open at a time).
    const away = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node) && !anchor?.contains(e.target as Node)) onClose(false); };
    // The menu is placed once, next to its button: close it when the page moves rather than leave it floating.
    const moved = (e: Event) => { if (!ref.current?.contains(e.target as Node)) onClose(false); };
    addEventListener("pointerdown", away, true);
    addEventListener("scroll", moved, true);
    addEventListener("resize", moved);
    return () => { removeEventListener("pointerdown", away, true); removeEventListener("scroll", moved, true); removeEventListener("resize", moved); };
  }, [onClose, anchor]);

  const act = async (k: Key) => {
    const name = fileSlug(item.config.title ?? item.config.settings?.labels.title ?? title);
    const chart = chartInstances.get(item.id);
    const view = widgetViews.get(item.id);
    onClose(k !== "edit" && k !== "remove");
    try {
      if (k === "edit") chartEditorStore.getState().edit(item.id);
      if (k === "png" && chart) { const url = toPNG(chart); download(`${name}.png`, url, "image/png"); done(`Downloaded ${name}.png`, url); }
      if (k === "svg" && chart) {
        const svg = toSVG(chart.getOption(), chart.getWidth(), chart.getHeight(), item.config.settings?.themeId);
        download(`${name}.svg`, svg, "image/svg+xml");
        done(`Downloaded ${name}.svg`);
      }
      if (k === "csv" && view) {
        const ds = DATASETS[view.binding.datasetId];
        download(`${name}.csv`, toCSV(view.ds.shape, { title: view.title, source: ds.source, filters: view.filters || undefined, sample: !!view.ds.sample || !!ds.sample }), "text/csv");
        done(`Downloaded ${name}.csv`);
      }
      if (k === "link") {
        const params: Record<string, string> = {};
        const z = item.config.zoom;
        if (Array.isArray(z)) params.z = zoomParam(item.id, z);
        const url = chartLink(location.origin, location.pathname, item.id, params);
        try {
          if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
          await navigator.clipboard.writeText(url);
          done("Link copied · opens this chart with the same filters");
        } catch { done(`Copy this link: ${url}`); }   // no clipboard (insecure context, denied, or not supported)
      }
      if (k === "print") printWidget(item.id);
      if (k === "dup") {
        const copy = `${item.type}-${Date.now().toString(36)}`;
        overviewLayout.getState().apply((l) => [...l, { ...structuredClone(item), id: copy, y: Math.max(0, ...l.map((i) => i.y + i.h)) }], [copy], `${title} duplicated`);
        overviewStore.getState().loadDataset({ ...item, id: copy });
        done(`Duplicated ${title}`);
      }
      if (k === "add") onAddToDashboard?.(item);
      if (k === "remove") overviewStore.getState().removeWidget(item.id);
    } catch { done(`Couldn’t export ${title}`); }
  };

  const rows: ([Key, string, string, boolean] | "-")[] = [
    ["edit", "pencil", "Edit chart", isChart && !readOnly], "-",
    ["png", "image", "Download PNG", isChart && chartInstances.has(item.id)], ["svg", "file-code", "Download SVG", isChart && chartInstances.has(item.id)],
    ["csv", "file-spreadsheet", "Download CSV (data)", isChart && widgetViews.has(item.id)],
    ["link", "link", "Copy link to chart", true], ["print", "printer", "Print chart", true], "-",
    ["dup", "copy", "Duplicate", !readOnly], ["add", "layout-grid", "Add to a dashboard…", !!onAddToDashboard && !readOnly], "-",
    ["remove", "trash-2", "Remove", !readOnly],
  ];
  const vis = rows.filter((r) => r === "-" || r[3]);
  const shown = vis.filter((r, i) => r !== "-" || (i > 0 && vis[i - 1] !== "-" && i < vis.length - 1));
  const onKey = (e: React.KeyboardEvent) => {
    const btns = [...(ref.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const at = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") { e.stopPropagation(); onClose(true); }
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); btns[(at + (e.key === "ArrowDown" ? 1 : -1) + btns.length) % btns.length]?.focus(); }
    else if (e.key.toLowerCase() === "e" && isChart && !readOnly) { e.preventDefault(); void act("edit"); }
  };
  // Widgets clip their content, so the menu is drawn above the page, under the ⋯ button (right-aligned, flipped up near the bottom).
  const r = anchor?.getBoundingClientRect();
  const up = !!r && r.bottom + 360 > innerHeight;
  const style: React.CSSProperties = r ? { position: "fixed", right: Math.max(8, innerWidth - r.right), ...(up ? { bottom: innerHeight - r.top + 4 } : { top: r.bottom + 4 }) } : {};
  return createPortal(
    <ul ref={ref} className="widget-menu ov-menu" role="menu" aria-label={`${title} actions`} style={style} onKeyDown={onKey} onPointerDown={(e) => e.stopPropagation()}>
      {shown.map((r, i) => r === "-" ? <li key={`d${i}`} role="separator" className="ov-menu__sep" /> : (
        <li key={r[0]} role="none">
          <button role="menuitem" className={r[0] === "remove" ? "is-danger" : undefined} onClick={() => void act(r[0])}>
            <OvIcon name={r[1]} size={15} />{r[2]}{r[0] === "edit" && <kbd className="ov-menu__kbd">E</kbd>}
          </button>
        </li>
      ))}
    </ul>, document.body,
  );
}

/** Print chart: only this widget, on white (the print rules in app.css hide everything else). */
export function printWidget(id: string) {
  const el = document.querySelector<HTMLElement>(`[data-flip-key="${CSS.escape(id)}"]`);
  if (!el) return;
  el.classList.add("print-only");
  const off = () => { el.classList.remove("print-only"); removeEventListener("afterprint", off); };
  addEventListener("afterprint", off);
  window.print();
  setTimeout(off, 1000);
}
